import { gql } from '@apollo/client';
import { cloneDeep } from 'lodash';

import { prepareResponse } from '@work4all/data';

import { BusinessPartnerContactCombined } from '@work4all/models/lib/Classes/BusinessPartnerContactCombined.entity';
import { BzObjectRelation } from '@work4all/models/lib/Classes/BzObjectRelation.entity';
import { Calculation } from '@work4all/models/lib/Classes/Calculation.entity';
import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { DeliveryNote } from '@work4all/models/lib/Classes/DeliveryNote.entity';
import { Demand } from '@work4all/models/lib/Classes/Demand.entity';
import { InboundDeliveryNote } from '@work4all/models/lib/Classes/InboundDeliveryNote.entity';
import { InboundPosition } from '@work4all/models/lib/Classes/InboundPosition.entity';
import { InputErpAnhangAttachementsRelation } from '@work4all/models/lib/Classes/InputErpAnhangAttachementsRelation.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { ModifyShadowBzObjectResult } from '@work4all/models/lib/Classes/ModifyShadowBzObjectResult.entity';
import { Offer } from '@work4all/models/lib/Classes/Offer.entity';
import { Order } from '@work4all/models/lib/Classes/Order.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { InsertBomStyle } from '@work4all/models/lib/Enums/InsertBomStyle.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { CUSTOM_FIELDS_DATA } from '../../../../components/custom-fields/custom-fields-data';

const bpCombined: BusinessPartnerContactCombined<EMode.query> = {
  businessPartner: {
    id: null,
    businessPartnerType: null,
    data: {
      customer: {
        id: null,
        number: null,
        name: null,
        street: null,
        city: null,
        postalCode: null,
        website: null,
      },
      supplier: {
        id: null,
        number: null,
        name: null,
        street: null,
        city: null,
        postalCode: null,
        website: null,
      },
    },
  },
  contact: {
    id: null,
    displayName: null,
    name: null,
    firstName: null,
    role: null,
    layedOff: null,
    salutation: {
      id: null,
      isFemale: null,
      isMale: null,
    },
    phoneNumber: null,
    eMail: null,
    businessPartnerId: null,
    businessPartnerType: null,
  },
};

const positionCommonFields:
  | Position<EMode.query>
  | InboundPosition<EMode.query> = {
  id: null,
  positionKind: null,
  articleId: null,
  number: null,
  longtext: null,
  amount: null,
  unit: null,
  shortText: null,
  internalText: null,
  vat: null,
  measurement: null,
  weight: null,
  singleWeight: null,
  amoundPalettes: null,
  amountCarton: null,
  amountVe: null,
  factor: null,
  articleNumber: null,
  process: null,
  posId: null,
  partsListAmount: null,
};

const positionPriceFields: Position<EMode.query> = {
  totalPriceGross: null,
  totalPriceNet: null,
  singlePriceGross: null,
  insteadOfTotalPrice: null,
  singlePriceNet: null,
  discount: null,
  purchasePrice: null,
};

const additionalPositionFields: Position<EMode.query> = {
  ledgerAccount: {
    id: null,
    name: null,
    number: null,
  },
  ledgerAccountNumber: null,
  costCenter1: {
    id: null,
    name: null,
  },
  costCenter1Number: null,
  width: null,
  volume: null,
  totalVolume: null,
  length: null,
  purchasePriceSurcharge: null,
  minutePrice: null,
  startTime: null,
  endTime: null,
  article: {
    id: null,
    name: null,
    number: null,
  },
  dispositionStart: null,
  dispositionEnd: null,
  ownArticleNumber: null,
  deliveryDate: null,
  actualDeliveryDate: null,
  manufacturerNumber: null,
  supplierId: null,
  process: null,
  supplier: {
    id: null,
    name: null,
  },
  responsibleUser: {
    id: null,
    displayName: null,
  },
  editorUserId: null,
};

const positionFields: Position<EMode.query> = {
  ...positionCommonFields,
  ...positionPriceFields,
  ...additionalPositionFields,
};

const deliveryNotePositionFields: Position<EMode.query> = {
  ...positionCommonFields,
  ...additionalPositionFields,
};

const inboundPositionFields: InboundPosition<EMode.query> = {
  ...positionCommonFields,
  costCenterId: null,
  projectId: null,
};

const inboundDeliveryNotesProps: InboundDeliveryNote<EMode.query> = {
  note: null,
  number: null,
  status1: null,
  date: null,
  rtfHeadText: null,
  rtfFooterText: null,
  userId: null,
  user: {
    id: null,
    displayName: null,
    shortName: null,
  },
  user2Id: null,
  user2: {
    id: null,
    displayName: null,
    shortName: null,
  },
  outgoingDeliveryDate: null,
  ourSign: null,
  yourSign: null,
  projectId: null,
  project: {
    id: null,
    name: null,
    number: null,
  },
  businessPartnerContactCombined: bpCombined,
  businessPartnerId: null,
  businessPartnerType: null,
  businessPartnerText: null,
  contactId: null,

  frozen: null,
  serviceStartDate: null,
  serviceEndDate: null,
  erpAttachmentList: [
    {
      id: null,
      fileName: null,
      fileExtension: null,
      lastModificationDate: null,
      updateTime: null,
      originalFileName: null,
      fileInfos: {
        fileSize: null,
        fileEntityFilename: null,
        previewUrl: null,
        downloadUrl: null,
        previewMimeType: null,
        downloadMimeType: null,
      },
      user: {
        id: null,
        displayName: null,
      },
    },
  ],
  archivePdf: [
    {
      id: null,
      downloadUrl: null,
      mimeType: null,
      xML: null,
      fileInfos: {
        fileEntityFilename: null,
        downloadUrl: null,
        previewUrl: null,
        previewMimeType: null,
        downloadMimeType: null,
        fileServiceProviderInfos: {
          id: null,
          fileName: null,
          mimeType: null,
          fspUrl: null,
        },
      },
    },
  ],
  customFieldList: [CUSTOM_FIELDS_DATA],
};

const commonProps = {
  ...inboundDeliveryNotesProps,
  positionList: [{ ...positionFields, insteadOfTotalPrice: null }],
  additionalAddress1: bpCombined,
  additionalAddress1CompanyId: null,
  additionalAddress1CompanyType: null,
  additionalAddress1ContactId: null,
  additionalAddress1Text: null,
  additionalAddress2: bpCombined,
  additionalAddress2CompanyId: null,
  additionalAddress2CompanyType: null,
  additionalAddress2ContactId: null,
  additionalAddress2Text: null,
  additionalAddress3: bpCombined,
  additionalAddress3CompanyId: null,
  additionalAddress3CompanyType: null,
  additionalAddress3ContactId: null,
  additionalAddress3Text: null,
  paymentKind: {
    id: null,
    note: null,
  },
  currency: {
    id: null,
    name: null,
  },
  currencyId: null,
  paymentId: null,
  paymentDeadline: null,
  skonto: null,
  skontoDurationDays: null,
  deliveryKind: {
    id: null,
    text: null,
  },
  bankDetails: {
    id: null,
    name: null,
    bic: null,
    iban: null,
  },
  value: null,
  contractStartDate: null,
};

const commonPropsWithoutPrices = cloneDeep({
  ...commonProps,
  positionList: [deliveryNotePositionFields],
});
delete commonPropsWithoutPrices.value;

// TODO: missing props on InboundDeliveryNote, Calculation
const calculationMissingProps = {
  deliveryKindId: null,
  bankDetailsId: null,
};

const dispoProps = {
  dispositionStart: null,
  dispositionEnd: null,
};
const partialInvoiceLogic = {
  partialInvoiceLogicId: null,
  partialInvoiceLogic: {
    id: null,
    name: null,
  },
};

export const createResponseData = (
  entity: Entities,
  erpCommonProps: ModifyShadowBzObjectResult['data'] = {}
): ModifyShadowBzObjectResult<EMode.query>['data'] => {
  switch (entity) {
    case Entities.offer:
      return {
        offer: {
          ...erpCommonProps,
          ...commonProps,
          ...calculationMissingProps,
          ...dispoProps,
          ...partialInvoiceLogic,
          isClosed: null,
          validUntilDate: null,
        } as Offer<EMode.query>,
      };
    case Entities.deliveryNote:
      return {
        deliveryNote: {
          ...erpCommonProps,
          ...commonPropsWithoutPrices,
          ...calculationMissingProps,
          isClosed: null,
          signature: {
            id: null,
            name: null,
            fileType: null,
            info1: null,
            info2: null,
            fileInfos: {
              fileSize: null,
              fileEntityFilename: null,
              previewUrl: null,
              downloadUrl: null,
              previewMimeType: null,
              downloadMimeType: null,
            },
          },
        } as DeliveryNote<EMode.query>,
      };
    case Entities.invoice:
      return {
        invoice: {
          ...erpCommonProps,
          ...commonProps,
          ...calculationMissingProps,
          paid: null,
          ra: null,
        } as Invoice<EMode.query>,
      };
    case Entities.calculation:
      return {
        calculation: {
          ...erpCommonProps,
          ...commonProps,
          isClosed: null,
          ...dispoProps,
          validUntilDate: null,
        } as Calculation<EMode.query>,
      };
    case Entities.order:
      return {
        order: {
          ...erpCommonProps,
          ...commonProps,
          ...calculationMissingProps,
          isClosed: null,
          ...dispoProps,
        } as Order<EMode.query>,
      };
    case Entities.contract:
      return {
        contract: {
          ...erpCommonProps,
          ...commonProps,
          ...calculationMissingProps,
          isClosed: null,
          ...dispoProps,
          ...partialInvoiceLogic,
          contractDate: null,
          contractNumber: null,
        } as Contract<EMode.query>,
      };
    case Entities.demand:
      return {
        demand: {
          ...erpCommonProps,
          ...commonProps,
          isClosed: null,
          contractStartDate: null,
        } as Demand<EMode.query>,
      };
    case Entities.inboundDeliveryNote:
      return {
        inboundDeliveryNote: {
          ...erpCommonProps,
          ...inboundDeliveryNotesProps,
          isClosed: null,
          inboundDeliveryNoteKind: null,
          deliveryDate: null,
          deliveryNoteNumber: null,
          positionList: [inboundPositionFields],
        } as InboundDeliveryNote<EMode.query>,
      };

    default:
      throw new Error(`${entity} is not supported`);
  }
};

export const createResponse = (
  entity: Entities,
  erpCommonProps: ModifyShadowBzObjectResult['data'] = {}
): ModifyShadowBzObjectResult<EMode.query> => ({
  id: null,
  name: null,
  bzObjType: null,
  // The "id" field is not fetched on purpose. By doing this it will be
  // stored separately from the original entity in the Apollo cache. This
  // makes it so that changes to the shadow object aren't reflected in the
  // rest of the application until the shadowBzObject is finalized
  data: createResponseData(entity, erpCommonProps),
});

const createShadowBzObjectResponeGraphql = (
  entity: Entities,
  erpCommonProps: ModifyShadowBzObjectResult['data'] = {}
) =>
  prepareResponse(
    Entities.modifyShadowBzObjectResult,
    createResponse(entity, erpCommonProps)
  );

export const createShadowBzObjectGraphQl = (entity: Entities) => gql`
mutation CreateShadowBzObject(
  $type: BzObjType!
  $sdObjType: SdObjType
  $sdObjMemberCode: Int
  $bzObjMemberCode: Int
  $apCode: Int
  $name: String
) {
  createShadowBzObject(
    type: $type
    sdObjType: $sdObjType
    sdObjMemberCode: $sdObjMemberCode
    bzObjMemberCode: $bzObjMemberCode
    name: $name
    apCode: $apCode
  ) {
    ${createShadowBzObjectResponeGraphql(entity)}
  }
}
`;

export const modifyShadowBzObjectGraphQl = (entity: Entities) => gql`
mutation ModifyShadowBzObject(
  $id: ID!
  $deliveryNote: InputLieferschein
  $offer: InputAngebot
  $invoice: InputRechnung
  $calculation: InputKalkulation
  $contract: InputAuftrag
  $demand: InputBedarfsanforderung
  $order: InputBestellung
  $inboundDeliveryNote: InputEingangslieferschein
) {
  modifyShadowBzObject(
    id: $id
    lieferschein: $deliveryNote
    angebot: $offer
    rechnung: $invoice
    kalkulation: $calculation
    auftrag: $contract
    bedarf: $demand
    bestellung: $order
    eingangslieferschein: $inboundDeliveryNote
  ) {
    ${createShadowBzObjectResponeGraphql(entity)}
  }
}
`;

export type ModifyShadowBzObjectResponse = {
  modifyShadowBzObject: ModifyShadowBzObjectResult;
};

export type ModifyShadowBzObjectVars = {
  id: string | number;
  deliveryNote?: DeliveryNote;
};

export type CreateShadowBzObjectVars = {
  type: BzObjType;
  bzObjMemberCode?: number;
  sdObjType?: SdObjType;
  sdObjMemberCode?: number;
  name?: string | null;
  apCode?: number | null;
};

export type CreateShadowBzObjectResponse = {
  createShadowBzObject: ModifyShadowBzObjectResult;
};

export const modifyShadowBzObjectAddPositionGraphQl = (entity: Entities) => gql`
mutation ModifyShadowBzObjectAddPosition(
  $id: ID!
  $positionType: ErpPositionsArt!
  $articleId: Int
  $amount: Decimal
  $bomStyle: InsertBomStyle
) {
  modifyShadowBzObjectAddPosition(
    id: $id
    positionsArt: $positionType
    artikelCode: $articleId
    anzahl: $amount
    bomStyle: $bomStyle
  ) {
    ${createShadowBzObjectResponeGraphql(entity)}
  }
}
`;

export type ModifyShadowBzObjectAddPositionResponse = {
  modifyShadowBzObjectAddPosition: ModifyShadowBzObjectResult;
};

export type ModifyShadowBzObjectAddPositionVars = {
  id: string | number;
  positionType: ErpPositionsKind;
  articleId?: number | null;
  amount?: number | null;
  bomStyle?: InsertBomStyle;
};

export const modifyShadowBzObjectRemovePositionGraphQl = (
  entity: Entities
) => gql`
mutation ModifyShadowBzObjectRemovePosition($id: ID!, $positionId: Int!, $positionIds: [Int]) {
  modifyShadowBzObjectRemovePosition(id: $id, positionCode: $positionId,  positionCodes: $positionIds) {
    ${createShadowBzObjectResponeGraphql(entity)}
  }
}
`;

export type ModifyShadowBzObjectRemovePositionResponse = {
  modifyShadowBzObjectRemovePosition: ModifyShadowBzObjectResult;
};

export type ModifyShadowBzObjectRemovePositionVars = {
  id: string | number;
  positionId?: number;
  positionIds: number[];
};

export const modifyShadowObjectMovePositionGraphQl = (entity: Entities) => gql`
mutation ModifyShadowObjectMovePosition(
  $id: ID!
  $positionId: Int!
  $index: Int!
) {
  modifyShadowBzObjectMovePosition(
    id: $id
    positionCode: $positionId
    newIndex: $index
  ) {
    ${createShadowBzObjectResponeGraphql(entity)}
  }
}
`;

export type ModifyShadowBzObjectMovePositionResponse = {
  modifyShadowBzObjectMovePosition: ModifyShadowBzObjectResult;
};

export type ModifyShadowBzObjectMovePositionVars = {
  id: string | number;
  positionId: number;
  index: number;
};

const shadowBzObjectResponeGraphQlWithId = (entity: Entities) =>
  prepareResponse(
    Entities.modifyShadowBzObjectResult,
    createResponse(entity, { id: null })
  );

export const persistShadowBzObjectGraphQl = (entity: Entities) => gql`
  mutation PersistShadowBzObject($id: ID! $attachements: InputErpAnhangAttachementsRelation, $relations: BzObjectRelation) {
    persistShadowBzObject(id: $id, attachements: $attachements, relations: $relations, removeShadowBzObject: false) {
      ${shadowBzObjectResponeGraphQlWithId(entity)}
    }
  }
`;

export type PersistShadowBzObjectResponse = {
  persistShadowBzObject: ModifyShadowBzObjectResult;
};

export type PersistShadowBzObjectVars = {
  id: string;
  attachements?: InputErpAnhangAttachementsRelation;
  relations: BzObjectRelation;
};

export const DELETE_SHADOW_BZ_OBJECT = gql`
  mutation DeleteShadowBzObject($id: ID!) {
    deleteShadowBzObject(id: $id)
  }
`;

export type DeleteShadowBzObjectResponse = {
  deleteShadowBzObject: ModifyShadowBzObjectResult;
};

export type DeleteShadowBzObjectVars = {
  id: string | number;
};

export const modifyShadowBzObjectModifyPositionGraphQl = (
  entity: Entities
) => gql`
  mutation ModifyShadowBzObjectModifyPosition(
    $id: ID!
    $position: InputPosition!
  ) {
    modifyShadowBzObjectModifyPosition(id: $id, position: $position) {
      ${createShadowBzObjectResponeGraphql(entity)}
    }
  }
`;

export type ModifyShadowBzObjectModifyPositionResponse = {
  modifyShadowBzObjectModifyPosition: ModifyShadowBzObjectResult;
};

export type ModifyShadowBzObjectModifyPositionVars = {
  id: string | number;
  position: Position;
};
